import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { useTranslations } from "next-intl";
import { useQueryClient } from "@tanstack/react-query";
import { apiErrorText } from "@/lib/api-error";
import { clearPendingReferral } from "@/lib/pending-referral";
import { enrollInFreeCourse } from "@/services/api/courses";
import { createCheckout, validateCheckoutCoupon } from "@/services/api/checkout";

type EnrollmentState = "anonymous" | "not_enrolled" | "enrolled";

type PriceOption = {
  id: string;
  currency: string;
  displayAmount: string;
  gateway: "razorpay" | "stripe";
};

type CheckoutCurrency = "INR" | "USD";

export type CheckoutOfferProp = {
  available: boolean;
  currency: CheckoutCurrency | null;
  provider: "razorpay" | "stripe" | null;
  currencies: Array<{ currency: CheckoutCurrency; provider: "razorpay" | "stripe" }>;
  reason?: string;
};

type Props = {
  tenantSlug: string;
  courseId: string;
  courseSlug: string;
  free: boolean;
  enrollmentState: EnrollmentState;
  canSelfEnroll: boolean;
  prices: PriceOption[];
  /** PAY-1: what the tenant can actually take payment in. */
  checkout?: CheckoutOfferProp;
  /** PAY-8: the coupon box only when a coupon could apply. */
  couponsAvailable?: boolean;
  /** PAY-9: the referral code to send with the checkout (URL or remembered). */
  referralCode?: string | null;
  /** PAY-9: whether the server said it would credit that code. */
  referralAccepted?: boolean;
};

const NO_CHECKOUT: CheckoutOfferProp = {
  available: false,
  currency: null,
  provider: null,
  currencies: [],
};

export function CourseEnrollPanel({
  tenantSlug,
  courseId,
  courseSlug,
  free,
  enrollmentState,
  canSelfEnroll,
  prices,
  checkout = NO_CHECKOUT,
  couponsAvailable = false,
  referralCode,
  referralAccepted = false,
}: Props) {
  const t = useTranslations("course");
  const tDetail = useTranslations("courseDetail");
  const tRoot = useTranslations();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enrolled, setEnrolled] = useState(enrollmentState === "enrolled");
  const [currency, setCurrency] = useState<CheckoutCurrency | null>(checkout.currency);
  const [couponCode, setCouponCode] = useState("");
  const [discountPreview, setDiscountPreview] = useState<number | null>(null);

  // PAY-1: only currencies the tenant can charge in, and only those the course is priced in.
  const offered = checkout.currencies.filter((option) =>
    prices.some((price) => price.currency === option.currency),
  );
  const selected = offered.find((option) => option.currency === currency) ?? offered[0];
  const selectedPrice = prices.find((p) => p.currency === selected?.currency);

  async function handleEnroll() {
    setLoading(true);
    setError(null);
    try {
      await enrollInFreeCourse(courseId);
      setEnrolled(true);
      await queryClient.invalidateQueries({ queryKey: ["course-detail", tenantSlug, courseSlug] });
    } catch (err) {
      setError(apiErrorText(err, tRoot, "course.enrollmentFailed", { namespace: "enroll.errors" }));
    } finally {
      setLoading(false);
    }
  }

  async function handleCheckout() {
    if (!selected) return;
    setLoading(true);
    setError(null);
    try {
      const result = await createCheckout({
        courseId,
        currency: selected.currency,
        couponCode: couponsAvailable ? couponCode.trim() || undefined : undefined,
        referralCode: referralCode ?? undefined,
      });
      if (result.redirectUrl) {
        clearPendingReferral();
        const redirect = new URL(result.redirectUrl, window.location.origin);
        if (redirect.pathname.includes("/checkout/mock/")) {
          window.location.href = `${window.location.origin}${redirect.pathname}${redirect.search}`;
          return;
        }
        window.location.href = result.redirectUrl;
        return;
      }
      setError(t("checkoutNoRedirect"));
    } catch (err) {
      setError(apiErrorText(err, tRoot, "checkout.failed", { namespace: "checkout.errors" }));
    } finally {
      setLoading(false);
    }
  }

  async function handleValidateCoupon() {
    if (!couponCode.trim() || !selected) return;
    setError(null);
    try {
      const preview = await validateCheckoutCoupon({
        courseId,
        currency: selected.currency,
        code: couponCode.trim(),
      });
      setDiscountPreview(preview.finalAmount);
    } catch (err) {
      setDiscountPreview(null);
      setError(apiErrorText(err, tRoot, "checkout.couponFailed", { namespace: "checkout.errors" }));
    }
  }

  if (enrolled) {
    return (
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <p
          className="text-sm text-green-700 dark:text-green-400"
          data-testid="course-enrolled-badge"
        >
          {t("enrolled")}
        </p>
        <Link
          to={`/t/${tenantSlug}/learn/${courseSlug}`}
          className="inline-block rounded-full px-4 py-2 text-sm font-semibold text-white hover:brightness-110"
          style={{ backgroundColor: "var(--accent-solid)" }}
          data-testid="course-continue-learning"
        >
          {t("continueLearning")}
        </Link>
        <Link
          to={`/t/${tenantSlug}/learn/${courseSlug}/community`}
          className="inline-block rounded-full border border-[var(--border-strong)] px-4 py-2 text-sm font-semibold text-[var(--text-primary)] hover:bg-[var(--surface-3)]"
          data-testid="course-community-link"
        >
          {t("community")}
        </Link>
      </div>
    );
  }

  if (enrollmentState === "anonymous") {
    // PAY-9 / C2: come back to this course (and its ?ref=) after signing in.
    const next = `${location.pathname}${location.search}`;
    return (
      <Link
        to={`/t/${tenantSlug}/login?next=${encodeURIComponent(next)}`}
        className="mt-6 inline-block rounded-md bg-[var(--accent-strong)] px-4 py-2 text-sm text-[var(--accent-foreground)] hover:bg-[var(--accent-strong-hover)]"
        data-testid="course-detail-login"
      >
        {t("signInToEnroll")}
      </Link>
    );
  }

  if (free && canSelfEnroll) {
    return (
      <div className="mt-6">
        <button
          type="button"
          onClick={() => void handleEnroll()}
          disabled={loading}
          className="rounded-md bg-[var(--accent-strong)] px-4 py-2 text-sm text-white hover:bg-[var(--accent-strong-hover)] disabled:opacity-60"
          data-testid="course-enroll-free"
        >
          {loading ? t("enrolling") : t("enrollFree")}
        </button>
        {error && (
          <p role="alert" className="mt-2 text-sm text-red-600" data-testid="course-enroll-error">
            {error}
          </p>
        )}
      </div>
    );
  }

  if (!canSelfEnroll) {
    return <p className="mt-6 text-sm text-[var(--text-muted)]">{t("studentPurchaseHint")}</p>;
  }

  // PAY-1: nothing this tenant can charge for — say so instead of a Pay button that fails.
  if (!checkout.available || !selected) {
    return (
      <div className="mt-6" data-testid="course-checkout-unavailable">
        <p className="text-sm font-medium text-[var(--text-primary)]">
          {tDetail("purchasingUnavailable")}
        </p>
        <p className="mt-1 text-sm text-[var(--text-muted)]">
          {tDetail("purchasingUnavailableHint")}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-4" data-testid="course-checkout-panel">
      {offered.length > 1 ? (
        <fieldset>
          <legend className="text-sm font-medium text-[var(--text-secondary)]">{t("payIn")}</legend>
          <div className="mt-2 flex flex-wrap gap-3">
            {offered.map((option) => {
              const price = prices.find((p) => p.currency === option.currency);
              return (
                <label
                  key={option.currency}
                  className="flex cursor-pointer items-center gap-2 rounded-md border border-[var(--border-strong)] px-3 py-2 text-sm"
                  data-testid={`course-checkout-currency-${option.currency.toLowerCase()}`}
                >
                  <input
                    type="radio"
                    name="checkout-currency"
                    value={option.currency}
                    checked={selected.currency === option.currency}
                    onChange={() => {
                      setCurrency(option.currency);
                      setDiscountPreview(null);
                    }}
                  />
                  <span>
                    {option.currency} — {price?.displayAmount}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
      ) : null}
      {couponsAvailable ? (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-[var(--text-secondary)]">
            {t("couponCode")}
            <input
              className="mt-1 w-full rounded-md border border-[var(--border-strong)] bg-[var(--surface-1)] px-3 py-2 text-sm text-[var(--text-primary)]"
              value={couponCode}
              onChange={(event) => setCouponCode(event.target.value)}
              data-testid="course-checkout-coupon"
            />
          </label>
          <button
            type="button"
            onClick={() => void handleValidateCoupon()}
            disabled={loading || !couponCode.trim()}
            className="rounded-md border border-[var(--border-strong)] px-3 py-1.5 text-sm"
            data-testid="course-checkout-apply-coupon"
          >
            {t("applyCoupon")}
          </button>
          {discountPreview != null ? (
            <p
              className="text-sm text-green-700 dark:text-green-400"
              data-testid="course-checkout-discount"
            >
              {t("couponApplied", { amount: (discountPreview / 100).toFixed(2) })}
            </p>
          ) : null}
        </div>
      ) : null}
      {referralCode && referralAccepted ? (
        <p className="text-xs text-[var(--text-muted)]" data-testid="course-checkout-referral">
          {t("referralApplied", { code: referralCode })}
        </p>
      ) : null}
      <button
        type="button"
        onClick={() => void handleCheckout()}
        disabled={loading || !selectedPrice}
        className="rounded-md bg-[var(--accent-strong)] px-4 py-2 text-sm text-white hover:bg-[var(--accent-strong-hover)] disabled:opacity-60"
        data-testid={`course-checkout-${selected.provider}`}
      >
        {loading
          ? t("redirecting")
          : selected.provider === "razorpay"
            ? t("payRazorpay")
            : t("payStripe")}
      </button>
      {error && (
        <p role="alert" className="text-sm text-red-600" data-testid="course-checkout-error">
          {error}
        </p>
      )}
    </div>
  );
}
